import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
	ICmmsFilterWithDefaultData,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { SelectionModel } from '@angular/cdk/collections';
import { BehaviorSubject, combineLatest, from, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { startClassificationLoad } from '@app/pages/cmms/state/cmms.actions';
import { Store } from '@ngrx/store';
import { cmmsClassifications } from '@app/pages/cmms/state/cmms.selectors';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import { TreeControl } from '@angular/cdk/tree';
import { IEntity } from '../location-tree-filter/location-tree-filter.component';
import { LetDirective } from '@ngrx/component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { AuSearchInputComponent } from '../../../../../../shared/ui-components/au-search-input/au-search-input.component';
import { ClassificationTreeComponentComponent } from '../../../shared-controls/classification-tree-component/classification-tree-component.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgxTolgeeModule } from '@tolgee/ngx';

enum ClassificationTypeEnum {
	AT = 'AT',
	BCT = 'BCT',
	CCB = 'CCB',
}

const DEFAULT_CLASSIFICATION_TYPE = ClassificationTypeEnum.BCT;

@Component({
	selector: 'classification-filter',
	templateUrl: './classification-filter.component.html',
	styleUrls: ['./classification-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		LetDirective,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		AuSearchInputComponent,
		ClassificationTreeComponentComponent,
		MatCheckbox,
		MatProgressSpinner,
		AsyncPipe,
		NgxTolgeeModule,
	],
})
export class ClassificationFilterComponent
	implements ICmmsFilterComponent, OnInit, OnDestroy, ICmmsFilterWithDefaultData
{
	@Input() data: {
		activePage: CmmsPages;
		columnName: string;
		preselectedValue: string[];
	};
	@Output() filterValueChanged = new EventEmitter<ICmmsFilterEmittedValue>();

	searchText$ = new BehaviorSubject<string>('');
	selectedClassificationType$ = new BehaviorSubject<ClassificationTypeEnum>(
		DEFAULT_CLASSIFICATION_TYPE
	);
	// TODO: make common classification type table
	classificationTypes = [
		{ id: ClassificationTypeEnum.BCT, name: 'building_component_table' },
		{ id: ClassificationTypeEnum.CCB, name: 'component_codes' },
	];

	filterOptions$: Observable<IClassification[]> = this.store$.select(cmmsClassifications);

	selectionModel = new SelectionModel<IClassification>(
		true,
		[],
		true,
		(a, b) => a.pk === b.pk
	);

	private destroy$ = new Subject<void>();

	constructor(private store$: Store) {}

	ngOnInit(): void {
		const classificationChangeEvent$ = this.selectedClassificationType$.pipe(
			takeUntil(this.destroy$),
			distinctUntilChanged()
		);
		classificationChangeEvent$.subscribe(classificationType => {
			this.store$.dispatch(
				startClassificationLoad({
					queryParams: {
						type: classificationType,
					},
				})
			);
		});
		this.classificationNameFilteringSubscription(classificationChangeEvent$);

		this.selectionModel.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.filterValueChanged.emit(
				this.generateFilterValue(this.selectionModel.selected)
			);
		});

		if (this.data.preselectedValue) {
			this.store$
				.select<IClassification[]>(cmmsClassifications)
				.pipe(
					map(classifications => this.concatClassifications(classifications)),
					takeUntil(this.destroy$)
				)
				.subscribe(classifications => {
					this.data.preselectedValue.forEach(pk => {
						const classification = classifications.find(
							classification => classification.pk === pk
						);
						if (classification) {
							this.selectionModel.select(classification);
						}
					});
				});
		}
	}
	concatClassifications(classifications: IClassification[]) {
		return [
			{ pk: `exclude__AT`, type: 'AT', name: 'None', parentPk: null },
			{ pk: `exclude__BCT`, type: 'BCT', name: 'None', parentPk: null },
			{ pk: `exclude__CCB`, type: 'CCB', name: 'None', parentPk: null },
		].concat(classifications);
	}
	classificationNameFilteringSubscription(
		classificationChangeEvent$: Observable<ClassificationTypeEnum>
	) {
		const textSearchingEvent$ = this.searchText$.pipe(
			map(searchText => searchText.trim()),
			debounceTime(100),
			distinctUntilChanged()
		);
		const filterClassificationByType$ =
			this.store$.select<IClassification[]>(cmmsClassifications);

		this.filterOptions$ = combineLatest([
			textSearchingEvent$,
			classificationChangeEvent$,
			filterClassificationByType$,
		]).pipe(
			takeUntil(this.destroy$),
			map(([searchText, classificationType, classifications]) => {
				classifications = this.concatClassifications(classifications);
				const filteredClassificationByType = classifications.filter(
					classification => classification.type === classificationType
				);

				if (!searchText) return filteredClassificationByType;

				// remove tree structure for search results
				const searchTextLower = searchText.toLowerCase();
				const exactSearchLower = searchTextLower + ' ';

				const filteredClassificationByTypeAndInputText = filteredClassificationByType
					.filter(classification =>
						classification.name.toLowerCase().includes(searchTextLower)
					)
					.map(classification => {
						const nameLower = classification.name.toLowerCase();
						return {
							...classification,
							parentPk: null, // set null to remove tree structure
							isStart: +nameLower.startsWith(searchTextLower),
							isExact: +nameLower.startsWith(exactSearchLower),
						};
					});
				// show first items that starts with search text
				return filteredClassificationByTypeAndInputText.sort((a, b) => {
					return (b.isExact - a.isExact) * 10 + (b.isStart - a.isStart);
				});
			})
		);
	}

	toggleCheck(
		classification: IClassification,
		treeControl: TreeControl<IClassification>,
		event: { source: any; checked: boolean }
	) {
		const descendants = treeControl.getDescendants(classification) as IClassification[];
		const elements = [classification, ...descendants];
		if (event.checked) {
			this.selectionModel.select(...elements);
		} else {
			this.selectionModel.deselect(...elements);
		}
	}

	descendantsPartiallySelected(
		node: IClassification,
		treeControl: TreeControl<IClassification>
	) {
		const descendants = treeControl.getDescendants(node);
		const result = descendants.some(child => this.selectionModel.isSelected(child));
		return result && !this.descendantsAllSelected(node, treeControl);
	}

	descendantsAllSelected(
		node: IClassification,
		treeControl: TreeControl<IClassification>
	): boolean {
		const descendants = treeControl.getDescendants(node);
		return (
			(descendants.length &&
				descendants.every(child => this.selectionModel.isSelected(child))) ||
			(descendants.length === 0 && this.selectionModel.isSelected(node))
		);
	}
	generateFilterValue(options: IClassification[]): {
		columnName: string;
		label: string;
		data: string[];
	} {
		/* Label should be in specific format that is needed to show it in filter button. */
		const label = [];
		const data = [];

		options.forEach(option => {
			label.push(option.name);
			data.push(option.pk);
		});

		return {
			columnName: this.data.columnName,
			label: label.join('; '),
			data,
		};
	}

	getClassificationName = (entity: IEntity) => {
		const classificationProperty =
			'classificationsV2' in entity ? 'classificationsV2' : 'classifications';
		const classifications = entity[classificationProperty];
		return classifications.map(classification => classification);
	};

	getDefaultFilterValue(entity: IEntity[]) {
		if (!entity.length) {
			return;
		}
		const classificationList = entity.flatMap(entityItem => {
			return this.getClassificationName(entityItem);
		});
		const filterValue = this.generateFilterValue(classificationList) as any;
		return from(Promise.resolve(filterValue));
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
